import React from 'react';
import PropTypes from 'prop-types';

/**
 * SafeIframe — renders an `iframe` in normal runtime and a test-friendly placeholder in
 * unsafe/test environments.
 *
 * Behavior:
 * - If `globalThis.cy` (Cypress) or `globalThis.Cypress` is present, the component renders a `<span>` placeholder
 *   containing the `src` for testing and snapshot stability.
 * - Otherwise it renders a real `<iframe>` with the provided props.
 *
 * Notes:
 * - The placeholder `<span>` intentionally receives the same `dataTest`, `title`, `style`,
 *   `className` and a non-standard `src` attribute so tests can inspect the expected iframe URL
 *   without creating a real iframe in the test runner.
 *
 * Props:
 * @param {Object} props - Component props
 * @param {string} props.dataTest - Test selector string (required).
 * @param {string} props.src - iframe `src` URL (required).
 * @param {string} [props.title] - Optional title attribute for accessibility.
 * @param {Object} [props.style] - Optional inline style object applied to the element.
 * @param {string} [props.className] - Optional CSS class name applied to the element.
 *
 * @returns {JSX.Element} Either an `iframe` element (normal runtime) or a `span` placeholder (test/unsafe).
 * @component
 */
function SafeIframe({ dataTest, className, src, title, style }) {
  const unsafe = () => {
    return globalThis?.cy || globalThis?.Cypress;
  };

  if (unsafe()) {
    return (
      <span data-test={dataTest} title={title} src={src} style={style} className={className}>
        IFRAME CONTENT: {src}
      </span>
    );
  }

  return (
    <iframe data-test={dataTest} title={title} src={src} style={style} className={className} />
  );
}

SafeIframe.propTypes = {
  dataTest: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  title: PropTypes.string,
  style: PropTypes.object,
  className: PropTypes.string,
};

SafeIframe.defaultProps = {
  title: undefined,
  style: undefined,
  className: undefined,
};

export default SafeIframe;
